/**
 * A media element's sound read for a level meter (the MAM's player, the archive's preview). The audio device is woken
 * in a user's gesture, and the element is routed through it only once the device runs: an element routed into a device
 * that stays stopped (Safari, outside a gesture) would be silent, so until then it plays its own sound, with no meter.
 * An element can be routed once in its life: one graph per element, closed when the element goes.
 */
export const METER_FLOOR = -60;
/** A peak (0–1) in dBFS, down to the floor. */
export const toDb = (v: number): number => (v <= 0.000_01 ? METER_FLOOR : Math.max(METER_FLOOR, 20 * Math.log10(v)));

/** What a level meter reads: the peaks now, left and right, in dBFS. */
export interface LevelSource {
  peaks(): [number, number];
}

/** The peak an analyser holds now, in dBFS. */
function peakDb(a: AnalyserNode, data: Float32Array<ArrayBuffer>): number {
  a.getFloatTimeDomainData(data);
  let p = 0;
  for (const x of data) p = Math.max(p, Math.abs(x));
  return toDb(p);
}

/** A source up-mixed to stereo (a mono one reaches both meters) and split into two analysers. */
function stereoPair(ctx: AudioContext, src: AudioNode): [AnalyserNode, AnalyserNode] {
  const stereo = ctx.createGain();
  stereo.channelCount = 2;
  stereo.channelCountMode = 'explicit';
  stereo.channelInterpretation = 'speakers';
  const split = ctx.createChannelSplitter(2);
  const left = ctx.createAnalyser();
  const right = ctx.createAnalyser();
  left.fftSize = right.fftSize = 1024;
  src.connect(stereo);
  stereo.connect(split);
  split.connect(left, 0);
  split.connect(right, 1);
  return [left, right];
}

export class ElementLevels implements LevelSource {
  private el: HTMLMediaElement | null = null;
  private ctx: AudioContext | null = null;
  private pair: [AnalyserNode, AnalyserNode] | null = null;
  private readonly data = new Float32Array(1024);

  /** In a user's gesture (a click, a key, a touch) or on play: the device starts, and the element is routed once it runs. */
  wake(el: HTMLMediaElement | null): void {
    if (!el) return;
    // another element (the one before is gone): a graph of its own
    if (this.el !== el) this.close();
    this.el = el;
    try {
      this.ctx ??= new AudioContext();
      if (this.ctx.state === 'running') this.route();
      else
        void this.ctx.resume().then(
          () => this.route(),
          () => undefined,
        );
    } catch {
      /* no meter: the element plays its own sound */
    }
  }

  /** The peaks now, left and right, in dBFS: the floor while nothing is routed or the element is paused. */
  peaks(): [number, number] {
    const el = this.el;
    if (!this.pair || !el || el.paused) return [METER_FLOOR, METER_FLOOR];
    return [peakDb(this.pair[0], this.data), peakDb(this.pair[1], this.data)];
  }

  /** Whether the element's sound goes through the meter. */
  get routed(): boolean {
    return this.pair !== null;
  }

  close(): void {
    void this.ctx?.close().catch(() => undefined);
    this.ctx = null;
    this.pair = null;
    this.el = null;
  }

  private route(): void {
    const ctx = this.ctx;
    const el = this.el;
    if (this.pair || !ctx || !el || ctx.state !== 'running') return;
    try {
      const src = ctx.createMediaElementSource(el);
      src.connect(ctx.destination);
      this.pair = stereoPair(ctx, src);
    } catch {
      /* no meter: the element plays its own sound */
    }
  }
}

/**
 * A microphone's sound read for a level meter (a voice-over being recorded). The audio device is woken in the click
 * that starts the recording, before the microphone is asked for (Safari keeps one made later asleep); the microphone
 * reaches the meter only, never the speakers.
 */
export class StreamLevels implements LevelSource {
  private ctx: AudioContext | null = null;
  private pair: [AnalyserNode, AnalyserNode] | null = null;
  private readonly data = new Float32Array(1024);

  /** In the click that starts the recording. */
  wake(): void {
    try {
      this.ctx ??= new AudioContext();
      if (this.ctx.state !== 'running') void this.ctx.resume().catch(() => undefined);
    } catch {
      /* no meter: the recording goes on */
    }
  }

  /** The microphone, read from now on. */
  attach(stream: MediaStream): void {
    this.wake();
    const ctx = this.ctx;
    if (!ctx) return;
    try {
      this.pair = stereoPair(ctx, ctx.createMediaStreamSource(stream));
      // pulled by the output at no volume (older WebKit runs only what reaches it): nothing is heard
      const silent = ctx.createGain();
      silent.gain.value = 0;
      for (const a of this.pair) a.connect(silent);
      silent.connect(ctx.destination);
    } catch {
      this.pair = null;
    }
  }

  /** The peaks now, left and right, in dBFS: the floor while no microphone is read or the device is asleep. */
  peaks(): [number, number] {
    if (!this.pair || this.ctx?.state !== 'running') return [METER_FLOOR, METER_FLOOR];
    return [peakDb(this.pair[0], this.data), peakDb(this.pair[1], this.data)];
  }

  close(): void {
    void this.ctx?.close().catch(() => undefined);
    this.ctx = null;
    this.pair = null;
  }
}
